import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, Bell, MessageSquare, Activity, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';
import { getUnreadCount } from '@/utils/unread';

// Images (same as Sidebar)
const dash = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/dop.png";
const comp = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/company.png";
const prof = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/profile.png";
const DataRoom = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/data.png";
const fin = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/finicial.png";
const kpi = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/kpi.png";
const set = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/setting.png";
const ser = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/service.png";
const ir = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/ir score.png";
const rf = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/refer.png";

type NavItem = {
    labelKey: string;
    path?: string;
    iconPath?: string;
    lucideIcon?: React.ElementType;
};

type MobileNavProps = {
    onLogout?: () => void;
};

// Mirror the desktop Sidebar's view resolution.
function getServiceMode(): 'accounting' | 'consulting' {
    const explicit = localStorage.getItem('activeServiceView');
    if (explicit === 'accounting' || explicit === 'consulting') return explicit;
    const ss = localStorage.getItem('setupService');
    return (ss === 'operate' || ss === 'incorporate') ? 'accounting' : 'consulting';
}

const navComms: NavItem[] = [
    { labelKey: "sidebar.notifications", path: "/notifications", lucideIcon: Bell },
    { labelKey: "sidebar.messageAdmin", path: "/message-admin", lucideIcon: MessageSquare },
];

export default function MobileNav({ onLogout }: MobileNavProps) {
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const [mode, setMode] = useState<'accounting' | 'consulting'>(getServiceMode);
    useEffect(() => {
        const update = () => setMode(getServiceMode());
        window.addEventListener('storage', update);
        window.addEventListener('serviceViewChanged', update);
        return () => {
            window.removeEventListener('storage', update);
            window.removeEventListener('serviceViewChanged', update);
        };
    }, []);
    const isAccounting = mode === 'accounting';

    const [unread, setUnread] = useState(getUnreadCount());
    useEffect(() => {
        const upd = () => setUnread(getUnreadCount());
        window.addEventListener('connct-unread', upd);
        window.addEventListener('storage', upd);
        return () => { window.removeEventListener('connct-unread', upd); window.removeEventListener('storage', upd); };
    }, []);

    const navPrimary: NavItem[] = [
        { labelKey: "sidebar.dashboard", path: "/dashboard", iconPath: dash },
        { labelKey: "sidebar.company", path: "/company-profile", iconPath: comp },
        { labelKey: "sidebar.profile", path: "/profile", iconPath: prof },
        { labelKey: "sidebar.dataRoom", path: isAccounting ? "/accounting/data-room" : "/data-room", iconPath: DataRoom },
            { labelKey: "sidebar.articles", path: "/articles", lucideIcon: BookOpen },
    ];

    const navAnalytics: NavItem[] = isAccounting
        ? [
            { labelKey: "sidebar.financialHealth", path: "/financial-health-check", lucideIcon: Activity },
        ]
        : [
            { labelKey: "sidebar.irScore", path: "/ir-score", iconPath: ir },
            { labelKey: "sidebar.financials", path: "/financials", iconPath: fin },
            { labelKey: "sidebar.kpis", path: "/kpis", iconPath: kpi },
        ];

    const navServices: NavItem[] = isAccounting
        ? [
            { labelKey: "sidebar.services", path: "/services", iconPath: ser },
            { labelKey: "sidebar.referAndEarn", path: "/refer-and-earn", iconPath: rf },
            { labelKey: "sidebar.settings", path: "/settings", iconPath: set },
        ]
        : [
            { labelKey: "sidebar.referAndEarn", path: "/refer-and-earn", iconPath: rf },
            { labelKey: "sidebar.settings", path: "/settings", iconPath: set },
        ];

    const handleNavigation = (path?: string) => {
        if (path) {
            navigate(path);
            setIsOpen(false);
        }
    };

    const isActive = (path?: string) => {
        if (!path) return false;
        return location.pathname === path;
    };

    const renderItem = (item: NavItem) => (
        <li key={item.labelKey}>
            <button
                onClick={() => handleNavigation(item.path)}
                disabled={!item.path}
                className={`w-full text-left px-4 py-3 rounded-lg text-sm flex items-center ${isActive(item.path)
                    ? "bg-blue-50 text-blue-600 font-medium"
                    : "text-gray-700 hover:bg-gray-50"
                    }`}
            >
                {item.iconPath ? (
                    <img src={item.iconPath} alt="" className="w-5 h-5 mr-3 object-contain" />
                ) : item.lucideIcon ? (
                    <item.lucideIcon className="w-5 h-5 mr-3 shrink-0" />
                ) : (
                    <span className="w-5 h-5 mr-3 inline-block" />
                )}
                {t(item.labelKey)}
                {item.labelKey === 'sidebar.notifications' && unread > 0 && (
                    <span className="ms-auto min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-red-500 text-white text-[10px] font-black leading-none">{unread > 9 ? '9+' : unread}</span>
                )}
            </button>
        </li>
    );

    return (
        <div className="md:hidden bg-white border-b border-gray-200 sticky top-0 z-30">
            <div className="flex items-center justify-between px-4 py-3">
                {/* Logo */}
                <Logo className="h-8" to="/dashboard" />

                {/* Hamburger */}
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                    {isOpen ? <X size={24} /> : <Menu size={24} />}
                    {!isOpen && unread > 0 && (
                        <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white" />
                    )}
                </button>
            </div>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="border-t border-gray-100 bg-white absolute w-full left-0 shadow-lg max-h-[80vh] overflow-y-auto z-40">
                    <nav className="p-4 space-y-6">
                        {/* Primary Navigation */}
                        <ul className="space-y-1">
                            {navPrimary.map(renderItem)}
                        </ul>

                        {/* Analytics Section */}
                        <div>
                            <h3 className="px-4 text-xs font-bold text-gray-400 uppercase mb-2">Analytics</h3>
                            <ul className="space-y-1">
                                {navAnalytics.map(renderItem)}
                            </ul>
                        </div>

                        {/* Communications Section */}
                        <div>
                            <h3 className="px-4 text-xs font-bold text-gray-400 uppercase mb-2">Communications</h3>
                            <ul className="space-y-1">
                                {navComms.map(renderItem)}
                            </ul>
                        </div>

                        {/* Services Section */}
                        <div>
                            <ul className="space-y-1">
                                {navServices.map(renderItem)}
                            </ul>
                        </div>

                        {/* Sign Out */}
                        <div className="border-t border-gray-100 pt-4">
                            <button
                                onClick={() => {
                                    if (onLogout) onLogout();
                                    setIsOpen(false);
                                }}
                                className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2"
                            >
                                <LogOut size={20} />
                                <span>{t('sidebar.signOut')}</span>
                            </button>
                        </div>
                    </nav>
                </div>
            )}
        </div>
    );
}
